import { Story } from '../../context/AppContext';

export interface StoryWorld {
  id: string;
  title: string;
  serialized: boolean;
  chapters: Story[];
}

export interface StorySearchFilters {
  query: string;
  age: string;
  duration: string;
  mood: string;
}

export const emptyStoryFilters: StorySearchFilters = { query: '', age: '', duration: '', mood: '' };

export function groupStoryWorlds(stories: Story[]): StoryWorld[] {
  const worlds = new Map<string, StoryWorld>();
  stories.forEach((story) => {
    const serialized = Boolean(story.book_id && story.book_title && Number.isInteger(story.chapter_number) && (story.chapter_number || 0) > 0);
    const id = serialized ? 'book:' + story.book_id : 'story:' + story.story_id;
    const world = worlds.get(id) || { id, title: serialized ? story.book_title! : story.title, serialized, chapters: [] };
    if (!world.chapters.some((chapter) => chapter.story_id === story.story_id)) world.chapters.push(story);
    worlds.set(id, world);
  });
  worlds.forEach((world) => {
    if (world.serialized) world.chapters.sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0) || a.story_id.localeCompare(b.story_id));
  });
  return Array.from(worlds.values());
}

function normalize(text: string): string {
  return text.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
}

export function matchesStory(story: Story, filters: StorySearchFilters): boolean {
  const text = normalize([story.title, story.book_title, story.book_summary, story.chapter_title, story.description, story.summary, story.theme, ...(story.tags || [])].filter(Boolean).join(' '));
  const words = normalize(filters.query.trim()).split(/\s+/).filter(Boolean);
  if (!words.every((word) => text.includes(word))) return false;
  if (filters.age && story.age_range !== filters.age) return false;
  if (filters.mood && ![story.theme, ...(story.tags || [])].includes(filters.mood)) return false;
  const minutes = story.estimated_duration_minutes;
  if (filters.duration && (minutes == null || minutes <= 0)) return false;
  if (filters.duration === 'up_to_5' && minutes! > 5) return false;
  if (filters.duration === '6_to_10' && (minutes! < 6 || minutes! > 10)) return false;
  if (filters.duration === '11_to_15' && (minutes! < 11 || minutes! > 15)) return false;
  if (filters.duration === '16_plus' && minutes! < 16) return false;
  return true;
}

export function readChapterSelections(key: string): Record<string, string> {
  try {
    const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return Object.fromEntries(Object.entries(stored).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
  } catch { return {}; }
}

export function writeChapterSelections(key: string, selections: Record<string, string>): void {
  try { sessionStorage.setItem(key, JSON.stringify(selections)); } catch { /* Browsing also works with storage blocked. */ }
}
